import { yupResolver } from '@hookform/resolvers/yup';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
} from '@mui/material';
import { type RawTimeZone, rawTimeZones } from '@vvo/tzdb';
import _ from 'lodash';
import { type FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import AutocompleteOptions from '../AutocompleteOptions.js';
import { type PatientFormValues } from '../Forms/formTypes.js';
import { patientFormSchema } from '../Forms/validations.js';
import Editor from '../MUIEditor/Editor.js';

const defaultValues: PatientFormValues = {
  firstName: '',
  lastName: '',
  physicalAddress: '',
  timeZone: rawTimeZones.find((timeZone) => {
    return timeZone.name === 'America/New_York';
  }) as RawTimeZone,
  careInstructions: '',
  active: false,
};

interface PatientInformationFormProps {
  values?: PatientFormValues;
  onSubmit: (values: PatientFormValues, callback: () => void) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}
const PatientInformationForm: FC<PatientInformationFormProps> = ({ values, onSubmit, onDirtyChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues,
    values,
    resolver: yupResolver(patientFormSchema),
  });

  const isEditing = !!values;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Box>
      <Box>
        <form
          onSubmit={handleSubmit((data) => {
            setIsSubmitting(true);
            onSubmit(data, () => setIsSubmitting(false));
          })}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name={'firstName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors?.firstName}
                    helperText={errors?.firstName?.message || ''}
                    InputLabelProps={{ required: true }}
                    label={'First name'}
                  />
                )}
              />
              <Controller
                name={'lastName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors?.lastName}
                    helperText={errors?.lastName?.message || ''}
                    InputLabelProps={{ required: true }}
                    label={'Last name'}
                  />
                )}
              />
            </Stack>
            <Controller
              name={'physicalAddress'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  InputLabelProps={{ required: true }}
                  error={!!errors?.physicalAddress}
                  helperText={errors?.physicalAddress?.message || ''}
                  label={'Address'}
                />
              )}
            />
            <Controller
              name={'timeZone'}
              control={control}
              render={({ field }) => {
                return (
                  <Autocomplete
                    value={field.value}
                    onChange={(_: any, value: RawTimeZone | null) => field.onChange(value)}
                    id={'timezone-select'}
                    options={rawTimeZones}
                    isOptionEqualToValue={(option, value) => option.rawFormat === value.rawFormat}
                    getOptionLabel={(timeZone: RawTimeZone) => timeZone.rawFormat || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{ required: true }}
                        error={!!errors?.timeZone}
                        helperText={errors?.timeZone?.message || ''}
                        label={'Timezone'}
                      />
                    )}
                    renderOption={({ key, ...props }, option, { inputValue }) => (
                      <AutocompleteOptions option={option.rawFormat} value={inputValue} key={key} {...props} />
                    )}
                  />
                );
              }}
            />
            <Box>
              <InputLabel>Care instructions</InputLabel>
              <Controller
                name={'careInstructions'}
                control={control}
                render={({ field }) => (
                  <Editor onSubmit={(content: string) => field.onChange(content)} content={field.value} />
                )}
              />
              <FormHelperText>Notes, special requests, routines, etc.</FormHelperText>
            </Box>
            <Box>
              <Controller
                name={'active'}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                      />
                    }
                    label="Active"
                  />
                )}
              />
              <FormHelperText>Whether the client is currently paying for monitoring.</FormHelperText>
            </Box>

            <Divider />
            <Stack direction={'row'} justifyContent={'flex-end'} spacing={1}>
              {isEditing ? (
                <Button startIcon={<RestartAltIcon />} onClick={() => reset(values)} disabled={!isDirty}>
                  Reset
                </Button>
              ) : null}
              <LoadingButton
                type={'submit'}
                aria-label={isEditing ? 'Save details' : 'Save'}
                loading={isSubmitting}
                variant={'contained'}
                disabled={!_.isEmpty(errors) || !isDirty}
              >
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default PatientInformationForm;
